import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { ProductCard } from '../components/ProductCard';
import { FlashSaleCountdown } from '../components/FlashSaleCountdown';
import { productApi, categoryApi, flashSaleApi, seedApi } from '../lib/api';
import { toast } from 'sonner';

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initData = async () => {
      try {
        // First, seed the database
      //  try {
        //  await seedApi.seed();
       // } catch (e) {
          // Database might already be seeded
       // }

        // Load data
        const [productsRes, categoriesRes, flashSalesRes] = await Promise.all([
          productApi.getAll({ limit: 12 }),
          categoryApi.getAll(),
          flashSaleApi.getAll(true),
        ]);

        setProducts(productsRes.data);

        // Reorder categories: Academic first + 3 others
        const academicCategory = categoriesRes.data.find(
          (cat) => cat.name.toLowerCase() === "academic"
        );

        const otherCategories = categoriesRes.data
          .filter((cat) => cat.name.toLowerCase() !== "academic")
          .slice(0, 3);

        setCategories([academicCategory, ...otherCategories]);

        setFlashSales(flashSalesRes.data);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load products");
      } finally {
        setLoading(false);
      }
    };

    initData();
  }, []);

  const featuredProducts = products.filter((p) => p.featured);
  const activeFlashSale = flashSales[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-900 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-600">Loading store...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main>
        {/* Hero Section with Tetris Grid */}
        <section className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {/* Main Hero */}
            <div className="md:col-span-8 md:row-span-2 relative bg-emerald-900 overflow-hidden" data-testid="hero-section">
              <img
                src="../bica/shopper.jpg"
                alt="Happy Shopper"
                className="absolute inset-0 w-full h-full object-cover opacity-30"
              />
              <div className="relative z-10 p-8 md:p-12 h-full flex flex-col justify-end min-h-[300px] md:min-h-[400px]">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                  Shop Smart,<br />Live Better
                </h2>
                <p className="text-emerald-100 text-lg mb-6 max-w-md">

                  <p class="font-semibold text-emerald-100 text-lg mb-6 max-w-md leading-relaxed">
                    Discover quality products at unbeatable prices.
                    <br />
                    {/* <span class="font-semibold block mt-2 text-emerald-100 text-base">
                      Free delivery on orders over <span class="font-semibold">KES 5,000</span>.
                    </span> */}
                    {/* <span class="hidden text-sm text-emerald-300 italic ml-1">
                      *Terms and Conditions apply
                    </span> */}
                    <p class="font-semibold text-emerald-100 text-lg mb-6 max-w-md leading-relaxed">
                      For orders or enquiries call +254 118182959
                    </p>
                  </p>
                </p>
                <Link to="/products">
                  <span class="hidden text-sm text-emerald-300 italic ml-1">
                    *FREE delivery within Nairobi CBD
                  </span>
                  <Button className="bg-white text-emerald-900 hover:bg-stone-100 uppercase tracking-wider font-bold px-8 py-6 text-lg btn-shadow" data-testid="shop-now-btn">
                    Shop Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Flash Sale Banner */}
            {activeFlashSale && (
              <div className="md:col-span-4 md:row-span-2 bg-red-600 p-6 flex flex-col justify-between" data-testid="flash-sale-banner">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Zap className="h-6 w-6 text-yellow-300 fill-yellow-300" />
                    <span className="text-xl font-bold text-white uppercase tracking-wider">
                      Flash Sale
                    </span>
                  </div>
                  <FlashSaleCountdown endTime={activeFlashSale.end_time} />
                </div>

                {activeFlashSale.product && (
                  <div className="mt-6">
                    <div className="bg-white/10 rounded-lg p-4">
                      <img
                        src={activeFlashSale.product.images?.[0] || ''}
                        alt={activeFlashSale.product.name}
                        className="w-full h-32 object-contain mb-4"
                      />
                      <h3 className="text-white font-semibold mb-2 line-clamp-2">
                        {activeFlashSale.product.name}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-yellow-300">
                          KES {(activeFlashSale.product.price * (1 - activeFlashSale.discount_percent / 100)).toLocaleString()}
                        </span>
                        <span className="text-sm text-red-200 line-through">
                          KES {activeFlashSale.product.original_price.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <Link to={`/products/${activeFlashSale.product.id}`}>
                      <Button className="w-full mt-4 bg-white text-red-600 hover:bg-stone-100 font-bold uppercase" data-testid="flash-sale-btn">
                        Grab Deal
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Categories */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Shop by Category</h2>
            <Link to="/products" className="text-emerald-900 font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.slice(0, 4).map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group relative bg-white border border-stone-200 overflow-hidden aspect-square"
                data-testid={`category-${category.id}`}
              >
                <img
                  src={category.image_url || 'https://via.placeholder.com/400'}
                  alt={category.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-white font-bold text-lg">{category.name}</h3>
                  <p className="text-stone-300 text-sm">{category.product_count} products</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Featured Products */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-6 w-6 text-emerald-600" />
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Featured Products</h2>
            </div>
            <Link to="/products?featured=true" className="text-emerald-900 font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {featuredProducts.slice(0, 4).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                flashSale={flashSales.find((f) => f.product_id === product.id)}
              />
            ))}
          </div>
        </section>

        {/* All Products */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900">Latest Products</h2>
            <Link to="/products" className="text-emerald-900 font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.slice(0, 8).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                flashSale={flashSales.find((f) => f.product_id === product.id)}
              />
            ))}
          </div>
        </section>

        {/* Promo Banner */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-stone-900 p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Pay with Naofan
              </h3>
              <p className="text-stone-400">
                Turn your tokens into purchases. Fast, secure, seamless!
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* <div className="w-16 h-16 bg-light-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-3xl font-bold text-white">N</span>
              </div> */}
              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                <img src="/bica/avaratnao.jpg" alt="Logo" className="w-10 h-10" />
              </div>


              <div className="text-white">
                <p className="font-bold">Use Naofan Tpkens</p>
                <p className="text-sm text-stone-400">Instant confirmation</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <StoreFooter />
    </div>
  );
}
